import { IFilterState } from './types'
import { createFilterStore } from './filter-store'
import { QueryParam, tryParseObject } from '../utils'
import { EViewMode } from './view-mode.types'

export interface IViewDataQueryParams {
  filter?: string
  sk?: string
  mode?: string
}

export interface IViewDataHandlerDef<T> {
  filter?: IFilterState<T>
  seedKey?: string
  defaultFilter?: IFilterState<T>
  defaultSeedKey?: string
  defaultValue?: string
  viewMode?: EViewMode
  defaultViewMode?: EViewMode
}

export type IViewDataHandlerParams<T, K extends keyof IViewDataHandlerDef<T> = keyof IViewDataHandlerDef<T>> = Pick<IViewDataHandlerDef<T>, K>

export const syncQueryToURL = <T>(params: IViewDataHandlerParams<T, 'filter' | 'seedKey' | 'defaultFilter'>) => {
  // If window is undefined (e.g., during SSR), do nothing
  if (typeof window === 'undefined') return
  try {
    const updateParams: IViewDataQueryParams = { filter: '', sk: '' }

    // Handle filter
    if (!!params.filter && Object.keys(params.filter).length > 0) {
      const store = createFilterStore(params.filter, params.defaultFilter)
      const obj = store.extractFilter()
      const filterString = Object.keys(obj).length > 0 ? JSON.stringify(obj) : ''
      updateParams.filter = filterString
    }

    // Handle seedKey
    updateParams.sk = params.seedKey ?? ''

    // Update URL with both params at once
    if (updateParams.filter || updateParams.sk) {
      QueryParam.replacePatch<IViewDataQueryParams>(updateParams)
    } else {
      // If no params to update, remove existing filter and seedKey from URL
      QueryParam.replaceDeletes<IViewDataQueryParams>('filter', 'sk')
    }
  } catch (error) {
    console.error('Error syncing filter and seedKey to URL:', error)
  }
}

export const getQueryFromURL = <T>(params: IViewDataHandlerParams<T, 'defaultFilter' | 'defaultSeedKey'>) => {
  // If window is undefined (e.g., during SSR), return null
  if (typeof window === 'undefined') return null

  // Attempt to parse filter and seedKey from URL
  try {
    const urlParams = QueryParam.gets<IViewDataQueryParams>('filter', 'sk')
    const filter = tryParseObject<IFilterState<T>>(urlParams.filter, params.defaultFilter ?? {})
    return {
      filter,
      seedKey: urlParams.sk ?? params.defaultSeedKey ?? new Date().getTime().toString()
    }
  } catch (error) {
    console.error('Error getting filter from URL:', error)
    return null
  }
}

// Methods for managing seedKey in URL
export const getSeedKeyFromURL = <T>(params: IViewDataHandlerParams<T, 'defaultSeedKey'>): string => {
  // If window is undefined (e.g., during SSR), return defaultSeedKey or current timestamp
  if (typeof window === 'undefined') return params.defaultSeedKey ?? new Date().getTime().toString()
  try {
    const q = QueryParam.gets<IViewDataQueryParams>('sk')
    return q.sk ?? params.defaultSeedKey ?? new Date().getTime().toString()
  } catch (error) {
    console.error('Error getting seed key from URL:', error)
    return params.defaultSeedKey ?? new Date().getTime().toString()
  }
}

export const syncViewModeToURL = <T>(params: IViewDataHandlerParams<T, 'viewMode' | 'defaultViewMode'>) => {
  // If window is undefined (e.g., during SSR), do nothing
  if (typeof window === 'undefined') return
  try {
    const updateParams: IViewDataQueryParams = {
      mode: params.viewMode !== params.defaultViewMode ? params.viewMode ?? '' : ''
    }

    // Update URL with view mode
    if (updateParams.mode) {
      QueryParam.replacePatch<IViewDataQueryParams>(updateParams)
    } else {
      // If no view mode to update, remove existing mode from URL
      QueryParam.replaceDeletes<IViewDataQueryParams>('mode')
    }
  } catch (error) {
    console.error('Error syncing view mode to URL:', error)
  }
}

export const getViewModeFromURL = <T>(params: IViewDataHandlerParams<T, 'defaultViewMode'>): EViewMode | undefined => {
  // If window is undefined (e.g., during SSR), return defaultViewMode
  if (typeof window === 'undefined') return params.defaultViewMode
  try {
    const q = QueryParam.gets<IViewDataQueryParams>('mode')
    return q.mode ? (q.mode as EViewMode) : params.defaultViewMode
  } catch (error) {
    console.error('Error getting view mode from URL:', error)
    return params.defaultViewMode
  }
}

export const convertFilterToQueryParam = <T>(filterState?: IFilterState<T>) => {
  const obj = { ...(filterState || {}) }
  if (!!obj.details) delete obj.details
  return QueryParam.stringify<IViewDataQueryParams>({ filter: JSON.stringify(obj) })
}
